import { Box } from "@mui/material";
import type { MouseEvent } from "react";
import { usePermissionChecker } from "./PermissionContext";
import { useSupportAdminWriteGuard } from "./useSupportAdminWriteGuard";
import { PermissionAction } from "./types";
import type { PermissionGateProps } from "./types";

const WRITE_GATED_INTERACTIVE_SELECTOR = [
  "button",
  "a[href]",
  "input",
  "select",
  "textarea",
  "modus-wc-button",
  "modus-wc-menu-item",
  "[role='menuitemradio']",
  "modus-wc-switch",
  "[role='button']",
  "[role='menuitem']",
  "[role='switch']",
  "[type='submit']",
].join(", ");

const NOOP_GUARDED_ACTION = () => undefined;

export const PermissionGate = ({
  resource,
  action,
  children,
  fallback = null,
  slot,
}: PermissionGateProps) => {
  const { hasPermission } = usePermissionChecker();
  const { guardWriteAction } = useSupportAdminWriteGuard();

  const handleWriteGuardClickCapture = (event: MouseEvent<HTMLElement>) => {
    if (action === PermissionAction.View) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const interactiveTarget = target.closest(WRITE_GATED_INTERACTIVE_SELECTOR);
    if (!interactiveTarget) {
      return;
    }

    const isAllowed = guardWriteAction(action, NOOP_GUARDED_ACTION);
    if (!isAllowed) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  if (!hasPermission(resource, action)) {
    return fallback;
  }

  if (action === PermissionAction.View) {
    return children;
  }

  return (
    <Box
      component="div"
      slot={slot}
      sx={{ display: "contents" }}
      onClickCapture={handleWriteGuardClickCapture}
    >
      {children}
    </Box>
  );
};
